import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import style from "./style.module.css";
import { API_BASE } from "../../config/api";
import { preSessionPost } from "../../utils/api";
import { MIN_PASSWORD_LENGTH, isValidNewPassword } from "../../utils/passwordValidation";
import BrandMark from "../../Components/BrandMark";

// Pre-login, self-service password reset for POS staff (Owner/Admin/Sales).
// Mirrors ProfilePage's existing logged-in password-change step-state shape
// (idle/sending/code_sent/saving) — the closest precedent in this app,
// adapted for a pre-login form (no auth cookie, no authenticatedFetch).
// See docs/pos-staff-access-and-forgot-password-plan.md.
const ForgotPasswordPage: FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<"request" | "code_sent" | "resetting">("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState({ new: "", confirm: "" });
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setMsg({ type: "error", text: "Enter your email address." });
      return;
    }
    setSubmitting(true);
    setMsg(null);
    try {
      // Always 200 on success (no-enumeration design — see
      // userController.forgotPasswordStaff) — the same generic message is
      // shown whether or not the email matched an account.
      await preSessionPost(`${API_BASE}/user/forgot-password`, { email: email.trim() });
      setStep("code_sent");
      setMsg({ type: "success", text: `If an account exists for ${email.trim()}, a verification code has been sent.` });
    } catch (err: any) {
      // preSessionPost wraps axios, which rejects on any non-2xx response
      // (unlike ProfilePage's fetch-based pattern) — surface the real
      // server message (e.g. accountLimiter's 429 "Too many attempts...")
      // instead of always claiming the backend is unreachable. Mirrors
      // handleResetPassword's catch block below.
      setMsg({ type: "error", text: err.response?.data?.message || "Cannot reach the server. Make sure the backend is running." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setMsg({ type: "error", text: "Enter the verification code from your email." });
      return;
    }
    if (!isValidNewPassword(pwd.new)) {
      setMsg({ type: "error", text: `New password must be at least ${MIN_PASSWORD_LENGTH} characters.` });
      return;
    }
    if (pwd.new !== pwd.confirm) {
      setMsg({ type: "error", text: "Passwords do not match." });
      return;
    }

    setStep("resetting");
    setMsg(null);
    try {
      // preSessionPost (axios) rejects on any non-2xx response, and
      // resetPasswordStaff never returns 200 with success:false — a
      // resolved call here is unconditionally a success, matching how
      // AuthenticationPage's own axios-based login call treats a resolved
      // response. All failure handling lives in the catch block below.
      await preSessionPost(`${API_BASE}/user/reset-password`, {
        email: email.trim(),
        reset_token: code.trim(),
        new_password: pwd.new,
      });
      setMsg({ type: "success", text: "Password reset successfully. Redirecting to login…" });
      setTimeout(() => navigate("/auth", { replace: true }), 1200);
    } catch (err: any) {
      setStep("code_sent");
      setMsg({ type: "error", text: err.response?.data?.message || "Failed to reset password." });
    }
  };

  const handleStartOver = () => {
    setStep("request");
    setCode("");
    setPwd({ new: "", confirm: "" });
    setMsg(null);
  };

  return (
    <div className={style.container}>
      <div className={style.leftPanel}>
        <div className={style.brandSection}>
          <div className={style.brandLogoCircle}><BrandMark variant="dark" size={36} /></div>
          <h1 className={style.brandTitle}>1Markaz</h1>
          <p className={style.brandSubtitle}>Point of Sale Management System</p>
        </div>
      </div>

      <div className={style.rightPanel}>
        <div className={style.form}>
          <h1 className={style.formTitle}>Reset your password</h1>
          <p className={style.formSubtitle}>
            {step === "request"
              ? "Enter your account email to receive a verification code."
              : "Enter the code sent to your email and choose a new password."}
          </p>

          {msg && (
            <div className={`${style.alert} ${msg.type === "success" ? style.alertSuccess : style.alertError}`}>
              {msg.text}
            </div>
          )}

          {step === "request" && (
            <form onSubmit={handleRequestCode}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className={style.input}
                placeholder="Enter Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
              />
              <button type="submit" className={style.button} disabled={submitting}>
                {submitting ? "Sending…" : "Send Code"}
              </button>
            </form>
          )}

          {(step === "code_sent" || step === "resetting") && (
            <form onSubmit={handleResetPassword}>
              <label htmlFor="code">Verification Code</label>
              <input
                id="code"
                className={style.input}
                placeholder="Enter the 6-digit code from your email"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
                autoFocus
                disabled={step === "resetting"}
              />
              <label htmlFor="new-password">New Password</label>
              <input
                id="new-password"
                type="password"
                className={style.input}
                placeholder="Min. 6 characters"
                value={pwd.new}
                onChange={(e) => setPwd((p) => ({ ...p, new: e.target.value }))}
                autoComplete="new-password"
                disabled={step === "resetting"}
              />
              <label htmlFor="confirm-password">Confirm New Password</label>
              <input
                id="confirm-password"
                type="password"
                className={style.input}
                placeholder="Repeat new password"
                value={pwd.confirm}
                onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                autoComplete="new-password"
                disabled={step === "resetting"}
              />
              <button type="submit" className={style.button} disabled={step === "resetting"}>
                {step === "resetting" ? "Resetting…" : "Reset Password"}
              </button>
              <button type="button" className={style.secondaryButton} onClick={handleStartOver} disabled={step === "resetting"}>
                Start over
              </button>
            </form>
          )}

          <button
            type="button"
            className={style.backToLogin}
            onClick={() => navigate("/auth")}
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
